import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Form,
  Label,
  Input,
  Button,
  Spinner,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import CommonDataTable from "../../../Components/Common/Table/CommonDataTable";
import usePermissions from "../../../Components/Hooks/usePermissions";
import { useDispatch, useSelector } from "react-redux";
import ColumnSelectionModal from "../../../Components/Common/ColumnSelectionModal/ColumnSelectionModal";
import { fetchContactSupportList, fetchcontactSupportTableColumnNames, updateUserPreference } from "../../../slices/thunks";
import TransactionsModal from "../../../Components/Common/TransactionsModal";

const ContactSupport = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [searchTerm, setSearchTerm] = useState("");
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedColumns, setSelectedColumns] = useState([]); 
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [requestType, setRequestType] = useState();
    const today = new Date().toISOString().split("T")[0];

    const actionConfig = {
        showCheckboxColumn: false,
        action: false
    };

    const permission = (() => {
         return usePermissions(1, 25);
    })();

    const { tableColumnNames, loading, headerLoading, contactSupportList } = useSelector(
        (state) => ({
            tableColumnNames: state.ContactSupport.tableColumnNames,
            loading: state.ContactSupport.loading,
            headerLoading: state.ContactSupport.headerLoading,
            contactSupportList: state.ContactSupport.contactSupportList,
        })
    );

    const handleSearch = (e) => {
        e.preventDefault();

        const payload = {};
        if (fromDate) payload.FromDate = fromDate;
        if (toDate) payload.ToDate = toDate;
        if (requestType) payload.RequestType = requestType;

        dispatch(fetchContactSupportList(payload));
    };

    const handleClear = (e) => {
        e.preventDefault();
        setFromDate("");
        setToDate("");
        setRequestType();
        dispatch(fetchContactSupportList());
    };
    
    const saveUserPreferences = async () => {
        const preferencesPayload = {
        ColumnIDs: tableColumnNames.reduce((acc, column) => {
            acc[column.ID] = selectedColumns.includes(column.ID);
            return acc;
        }, {}),
        };

        setModalOpen(false);
        try {
        await dispatch(updateUserPreference(preferencesPayload)).unwrap();
        await dispatch(
            fetchcontactSupportTableColumnNames({
            module: "CONTACTSUPPORT",
            })
        );

        } catch (error) {
        console.error("Error updating preferences:", error);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                dispatch(fetchContactSupportList()); 
                dispatch(fetchcontactSupportTableColumnNames({ module: "CONTACTSUPPORT" }));
            } catch (error) {
                console.error(error);
            }   
        };

        fetchData();
    }, [dispatch]);

    document.title = "Alitqan | AI Chat Report";

    return (
        <React.Fragment>
        <div className="page-content">
            <Container fluid={true}>
            <Row className="mb-3">
                <Col>
                    <BreadCrumb title={t("ContactSupport")} />
                </Col>
            </Row>

            <Row>
                <Col lg={12}>
                <Card>
                    <CardHeader>
                    <h5 className="card-title mb-0">{t("SearchFilters")}</h5>
                    </CardHeader>
                    <CardBody>
                    <Form onSubmit={handleSearch}>
                        <Row className="g-3">

                        <Col md={4}>
                            <Label>{t("DateFromTo")}</Label>
                            <div className="d-flex gap-2">
                                {/* // --- Modified: Added value and onChange --- */}
                                <Input type="date" name="dateFrom" value={fromDate} onChange={(e) => setFromDate(e.target.value)} max={today} />
                                <Input type="date" name="dateTo" value={toDate} onChange={(e) => setToDate(e.target.value)} max={today} />
                            </div>
                        </Col>
                        
                        <Col md={4}>
                            <Label>{t("Request Type")}</Label>
                            <Input
                                type="select"
                                className="form-select"
                                value={requestType}
                                onChange={(e) => setRequestType(e.target.value)}
                            >
                                <option value=''>{t("Select Request Type")}</option>
                                <option value='1'>{t("General Inquiry")}</option>
                                <option value='2'>{t("Subscription Inquiry")}</option>
                            </Input>
                        </Col>

                        <Col md={12} className="text-end ">
                            {/* // --- Modified: Added onClick handlers --- */}
                            <Button type="submit" color="primary" className="me-2">
                                <i className="ri-search-line align-bottom me-1"></i> {t("Search")}
                            </Button>
                            <Button color="light" className="me-2" onClick={handleClear}>
                                <i className="ri-close-circle-line align-bottom me-1"></i> {t("Clear")}
                            </Button>
                        </Col>

                        
                        </Row>
                    </Form>
                    </CardBody>
                </Card>
                </Col>
            </Row>

            <div className="table-responsive">
                <Card>
                    <CardHeader>
                        <Row className="g-4 ">
                            <Col className="col-sm-auto">
                            <div>
                                <h5 className="card-title mb-0">{t("Contacts")}</h5>
                            </div>
                            </Col>
                            <Col className="col-sm">
                            <div className="d-flex justify-content-sm-end">
                                <Button
                                    className="btn btn-soft"
                                    onClick={() => setModalOpen(true)}
                                    disabled={loading || headerLoading}
                                >
                                <i className="ri-filter-3-line"></i>
                                </Button>
                                <div className="search-box ms-2 me-2">
                                <Input
                                    type="text"
                                    className="form-control search"
                                    placeholder={t("Search here")}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                <i className="ri-search-line search-icon"></i>
                                </div>
                            </div>
                            </Col>
                        </Row>
                    </CardHeader>
                    <CardBody>
                        {   (loading || headerLoading) ? (
                        <div className="d-flex justify-content-center align-items-center py-5">
                            <Spinner style={{ height: "3rem", width: "3rem" }} className="me-2">
                            Loading...
                            </Spinner>
                        </div>
                        ) : (
                        <div className="listjs-table" id="customerList">
                            <CommonDataTable
                            data={contactSupportList}
                            tableColumnNames={tableColumnNames}
                            searchTerm={searchTerm}
                            actionConfig={actionConfig}
                            permission={permission}
                            />
                        </div>
                        )}
                    </CardBody>
                </Card>
            </div>
            </Container>

            <ColumnSelectionModal
                isOpen={modalOpen}
                toggle={() => setModalOpen(!modalOpen)}
                columns={tableColumnNames}
                selectedColumns={selectedColumns}
                setSelectedColumns={setSelectedColumns}
                handleSave={saveUserPreferences}
            />
        </div>
        </React.Fragment>
    );
};

export default ContactSupport;